import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { useDeviceInfo } from '~Hooks';
import { CSSProperties } from 'styled-components';
import { checkBrowserAllowClick } from '~utilities';

/* Use these styles in the first wrapper inside the Modal component to follow similar design of other modals. Example:
const Wrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
`;
*/
export const insideWrapperStyles = {
  boxShadow: '0px 1px 18px var(--colorDimmerLight)',
  borderRadius: '8px',
  backgroundColor: 'var(--colorBlueGray1200)',
};

const Wrapper = styled.div<{
  position: 'center' | 'flex-start' | 'flex-end';
  animateOut: boolean;
  noPadding?: boolean;
  zIndex?: number;
  isPhone: boolean;
}>`
  position: fixed;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ zIndex }) => zIndex || 99};
  padding: ${({ noPadding }) => (noPadding ? '0' : '85px 16px')};

  @media screen and (max-width: 400px) {
    padding: ${({ noPadding, isPhone }) => (noPadding ? '0' : isPhone ? '24px 16px' : '16px')};
  }

  display: flex;
  flex-flow: column nowrap;
  overflow: auto;
  justify-content: ${({ position }) => position};
  align-items: center;
  box-sizing: border-box;
  animation: ${({ animateOut }) =>
    animateOut ? 'fade-out 0.1s linear forwards' : 'fade-in 0.1s linear forwards'};

  div,
  button {
    z-index: 1;
  }
`;

export const Backdrop = styled.div`
  position: fixed;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  background-color: var(--colorBlueGray1300);
  opacity: 0.75;
`;

interface Props {
  isShowing: boolean;
  hide?: () => void;
  backdropClose?: boolean;
  position?: 'center' | 'flex-start';
  noPadding?: boolean;
  zIndex?: number;
  style?: CSSProperties;
}

const Modal: React.FC<Props> = ({
  isShowing,
  hide,
  backdropClose = true,
  position,
  children,
  noPadding,
  zIndex,
  style,
}) => {
  const [keyboardIsOpen, setKeyboardIsOpen] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isStorybook = !!process.env.STORYBOOK_MODE;
  const deviceInfo = useDeviceInfo();
  const allowBrowserClick = checkBrowserAllowClick();

  useEffect(() => {
    if (position) return;

    if (ref.current) {
      const input = ref.current.querySelector('textarea') || ref.current.querySelector('input');
      const isFocused = document.activeElement === input;

      if (isFocused) {
        setKeyboardIsOpen(true);
      } else {
        setKeyboardIsOpen(false);
      }

      if (input) {
        input.addEventListener('blur', () => {
          setKeyboardIsOpen(false);
        });

        input.addEventListener('focus', () => {
          setKeyboardIsOpen(true);
        });
      }
    }
  }, [ref, children]);

  useEffect(() => {
    if (!keyboardIsOpen) {
      return;
    }

    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  }, [keyboardIsOpen]);

  useEffect(() => {
    if (!isShowing) {
      setKeyboardIsOpen(false);
    }
  }, [isShowing]);

  const handleClose = () => {
    setAnimateOut(true);

    setTimeout(() => {
      setAnimateOut(false);
      hide && hide();
    }, 200);
  };

  return isShowing
    ? createPortal(
        <Wrapper
          className="portal-modal"
          position={position ? position : 'center'}
          ref={ref}
          animateOut={animateOut}
          noPadding={noPadding}
          zIndex={zIndex}
          style={style}
          isPhone={Boolean(deviceInfo?.isPhone)}>
          <Backdrop
            onClick={(e) => {
              if (!allowBrowserClick) {
                return;
              }

              e.stopPropagation();
              if (backdropClose) {
                handleClose();
              }
            }}
            onTouchStart={backdropClose ? handleClose : undefined}
          />
          {children}
        </Wrapper>,
        document.body,
      )
    : null;
};

export default Modal;
